/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAvailableIncidentActions } from '../../shared/hooks/useAvailableIncidentActions'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { startTimer, trackEvent } from '@/utils/analytics/useAnalytics'
import { useStore } from 'react-redux'

import { useApolloClient } from '@apollo/client'
import { Box, Icon, useDisclosure } from '@chakra-ui/react'

import { Button as DarkButton } from '@hivewatch/dark'

import { Share } from '@/components/icons'
import { Button } from '@/components/ui'
import {
  IncidentDocument,
  useCreateIncidentActionMutation,
} from '@/graphql/generated/hooks'
import { IncidentQuery } from '@/graphql/generated/operations'
import {
  IncidentActionType,
  IncidentWorkflowStatus,
} from '@/graphql/generated/schemas'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import type { AppState } from '@/redux/store'
import {
  closeSOPIncidentDrawer,
  closeTraceLogDrawer,
  closeWorkflowDrawer,
  openSOPIncidentDrawer,
  openTraceLogDrawerForDevice,
  openTraceLogDrawerWithHighlight,
  openWorkflowDrawer,
  selectIncident,
  selectIncidentTraceLogDrawerState,
  selectIsIncidentDrawerOpen,
  selectSOPIncidentDrawerState,
  selectWorkflowDrawerIsOpen,
  setSOPIncidentNameLike,
  toggleIncidentTraceLogDrawer,
} from '@/redux/ui/uiSlice'
import { BUTTON_PRESS } from '@/utils/analytics'
import { includeIfActive } from '@/utils/apollo'
import { formatFullName } from '@/utils/formats'

import { ReleaseIncidentModal } from '../../components/ReleaseIncidentModal'
import { TakeOverIncidentModal } from '../../components/TakeOverIncidentModal'
import { MixpanelDataIProps } from '../../shared/types/types'

interface FeedButtonsIProps {
  incident: IncidentQuery['incident']
  mixpanelData: MixpanelDataIProps
}

export const FeedButtons = ({ incident, mixpanelData }: FeedButtonsIProps) => {
  const { shouldEnableIncidentActions } = usePermissions()
  const { showSuccess, showError } = useToasts({ preventDuplicate: false })
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)
  const colorScheme = useLightMode()
  const isWorkflowFeatureEnabled = useFeatureFlag(
    FeatureFlag.incidentResponseWorkflow
  )
  const dispatch = useAppDispatch()
  const isWorkflowDrawerOpen = useAppSelector(selectWorkflowDrawerIsOpen)
  const sopIncidentDrawerState = useAppSelector(selectSOPIncidentDrawerState)
  const traceLogDrawerState = useAppSelector(selectIncidentTraceLogDrawerState)
  const store = useStore<AppState>()
  const [createIncidentAction, { loading: isLoadingCreateIncidentAction }] =
    useCreateIncidentActionMutation()
  const client = useApolloClient()

  const { isAcknowledgeEnabled, isCanReleaseEnabled, isTakeOverEnabled } =
    useAvailableIncidentActions({ incident })

  const incidentId = incident?.id
  const currentOwner = formatFullName(incident?.owner ?? {}) || 'Unassigned'
  const hasWorkflowToOpenOnAcknowledge =
    Boolean(incident?.pendingWorkflow) ||
    (Boolean(incident?.workflowId) &&
      incident?.workflowStatus === IncidentWorkflowStatus.InProgress)
  const shouldOpenWorkflowOnAcknowledge =
    isWorkflowFeatureEnabled && hasWorkflowToOpenOnAcknowledge

  const {
    isOpen: isTakeOverOpen,
    onOpen: onTakeOverOpen,
    onClose: onTakeOverClose,
  } = useDisclosure()
  const {
    isOpen: isReleaseOpen,
    onOpen: onReleaseOpen,
    onClose: onReleaseClose,
  } = useDisclosure()

  const openWorkflowDrawerForAcknowledge = () => {
    if (!shouldOpenWorkflowOnAcknowledge || isWorkflowDrawerOpen) {
      return false
    }

    dispatch(closeSOPIncidentDrawer())
    dispatch(closeTraceLogDrawer())
    dispatch(openWorkflowDrawer())

    return true
  }

  const rollbackWorkflowDrawerForAcknowledge = ({
    acknowledgementIncidentId,
    didOpenWorkflowDrawer,
    isSOPIncidentDrawerOpenBeforeAck,
    isTraceLogDrawerOpenBeforeAck,
    previousSOPNameLike,
    previousTraceLogState,
  }: {
    acknowledgementIncidentId: string | undefined
    didOpenWorkflowDrawer: boolean
    isSOPIncidentDrawerOpenBeforeAck: boolean
    isTraceLogDrawerOpenBeforeAck: boolean
    previousSOPNameLike: string | null
    previousTraceLogState: {
      highlightedEvent: { detectedAt: string; externalId: string } | null
      deviceId: string | null
      shouldForceDeviceTab: boolean
    }
  }) => {
    const currentIncidentId = selectIncident(store.getState()).id
    if (
      !didOpenWorkflowDrawer ||
      currentIncidentId !== acknowledgementIncidentId
    ) {
      return false
    }

    dispatch(closeWorkflowDrawer())
    if (!selectIsIncidentDrawerOpen(store.getState())) {
      return false
    }

    if (isSOPIncidentDrawerOpenBeforeAck) {
      dispatch(openSOPIncidentDrawer())
      if (previousSOPNameLike !== null) {
        dispatch(setSOPIncidentNameLike(previousSOPNameLike))
      }
    }
    if (isTraceLogDrawerOpenBeforeAck) {
      const { highlightedEvent, deviceId, shouldForceDeviceTab } =
        previousTraceLogState
      if (shouldForceDeviceTab && deviceId) {
        // Restore a Reasoning Trail device deep-link (device filter + Device tab)
        dispatch(openTraceLogDrawerForDevice({ deviceId, highlightedEvent }))
      } else if (highlightedEvent) {
        dispatch(openTraceLogDrawerWithHighlight(highlightedEvent))
      } else {
        dispatch(toggleIncidentTraceLogDrawer())
      }
    }

    return true
  }

  const onAcknowledgementClick = async () => {
    const eventName = `${BUTTON_PRESS} Incident Acknowledge`
    startTimer(eventName)
    const acknowledgementIncidentId = incidentId
    const isSOPIncidentDrawerOpenBeforeAck = Boolean(
      sopIncidentDrawerState?.isOpen
    )
    const previousSOPNameLike = sopIncidentDrawerState?.sopNameLike ?? null
    const isTraceLogDrawerOpenBeforeAck = Boolean(traceLogDrawerState?.isOpen)
    const previousTraceLogState = {
      highlightedEvent: traceLogDrawerState?.highlightedEvent ?? null,
      deviceId: traceLogDrawerState?.deviceId ?? null,
      shouldForceDeviceTab: traceLogDrawerState?.shouldForceDeviceTab ?? false,
    }
    let didOpenWorkflowDrawer = false
    try {
      didOpenWorkflowDrawer = openWorkflowDrawerForAcknowledge()

      await createIncidentAction({
        variables: {
          input: {
            incidentId,
            type: IncidentActionType.AcknowledgementByOperator,
          },
        },
        // Not awaited so a failed refetch can't fail an already-committed action
        // (apollo-data-fetching.md). NextUpIncident keeps the Priority Queue
        // Next Up/Resume target fresh after this status change — included only
        // while it's mounted (PQ active) to avoid the "Unknown query" dev warning.
        refetchQueries: [
          { query: IncidentDocument, variables: { id: incidentId } },
          'GlobalUnresolvedIncidentsCount',
          ...includeIfActive(client, 'NextUpIncident'),
        ],
      })
      trackEvent(eventName, mixpanelData)
      showSuccess('Incident has been acknowledged.')
    } catch (e) {
      rollbackWorkflowDrawerForAcknowledge({
        acknowledgementIncidentId,
        didOpenWorkflowDrawer,
        isSOPIncidentDrawerOpenBeforeAck,
        isTraceLogDrawerOpenBeforeAck,
        previousSOPNameLike,
        previousTraceLogState,
      })
      showError(e)
    }
  }

  const onReleaseClick = async () => {
    trackEvent(`${BUTTON_PRESS} Incident Release`, mixpanelData)
    try {
      await createIncidentAction({
        variables: {
          input: {
            incidentId,
            type: IncidentActionType.ReleaseByOperator,
          },
        },
        // Not awaited so a failed refetch can't fail an already-committed action
        // (apollo-data-fetching.md). NextUpIncident keeps the Priority Queue
        // Next Up/Resume target fresh after this status change — included only
        // while it's mounted (PQ active) to avoid the "Unknown query" dev warning.
        refetchQueries: [
          { query: IncidentDocument, variables: { id: incidentId } },
          'GlobalUnresolvedIncidentsCount',
          ...includeIfActive(client, 'NextUpIncident'),
        ],
      })
      onReleaseClose()
      showSuccess('Incident has been released.')
    } catch (e) {
      showError(e)
    }
  }

  const onTakeOverClick = async () => {
    trackEvent(`${BUTTON_PRESS} Incident Take Over & Acknowledge`, mixpanelData)
    const acknowledgementIncidentId = incidentId
    const isSOPIncidentDrawerOpenBeforeAck = Boolean(
      sopIncidentDrawerState?.isOpen
    )
    const previousSOPNameLike = sopIncidentDrawerState?.sopNameLike ?? null
    const isTraceLogDrawerOpenBeforeAck = Boolean(traceLogDrawerState?.isOpen)
    const previousTraceLogState = {
      highlightedEvent: traceLogDrawerState?.highlightedEvent ?? null,
      deviceId: traceLogDrawerState?.deviceId ?? null,
      shouldForceDeviceTab: traceLogDrawerState?.shouldForceDeviceTab ?? false,
    }
    let didOpenWorkflowDrawer = false
    try {
      didOpenWorkflowDrawer = openWorkflowDrawerForAcknowledge()
      if (didOpenWorkflowDrawer) {
        onTakeOverClose()
      }

      await createIncidentAction({
        variables: {
          input: {
            incidentId,
            type: IncidentActionType.AcknowledgementByOperator,
          },
        },
        // Not awaited so a failed refetch can't fail an already-committed action
        // (apollo-data-fetching.md). NextUpIncident keeps the Priority Queue
        // Next Up/Resume target fresh after this status change — included only
        // while it's mounted (PQ active) to avoid the "Unknown query" dev warning.
        refetchQueries: [
          { query: IncidentDocument, variables: { id: incidentId } },
          'Incidents',
          ...includeIfActive(client, 'NextUpIncident'),
        ],
      })
      onTakeOverClose()
      showSuccess('Incident has been taken over & acknowledged.')
    } catch (e) {
      const didRollbackWorkflowDrawer = rollbackWorkflowDrawerForAcknowledge({
        acknowledgementIncidentId,
        didOpenWorkflowDrawer,
        isSOPIncidentDrawerOpenBeforeAck,
        isTraceLogDrawerOpenBeforeAck,
        previousSOPNameLike,
        previousTraceLogState,
      })
      if (didOpenWorkflowDrawer && didRollbackWorkflowDrawer) {
        onTakeOverOpen()
      }
      showError(e)
    }
  }
  return (
    <>
      {isAcknowledgeEnabled &&
        (isRebrand ? (
          <Box mt='4'>
            <DarkButton
              colorScheme={colorScheme}
              data-testid='incidentDrawer_feed_acknowledge'
              disabled={!shouldEnableIncidentActions}
              isLoading={isLoadingCreateIncidentAction}
              onClick={onAcknowledgementClick}
              size='lg'
              variant='primary'
            >
              Acknowledge
            </DarkButton>
          </Box>
        ) : (
          <Box bg='white' mt='4' pos='relative'>
            <Button
              data-testid='incidentDrawer_feed_acknowledge'
              isDisabled={!shouldEnableIncidentActions}
              isLoading={isLoadingCreateIncidentAction}
              loadingText='Acknowledging...'
              onClick={onAcknowledgementClick}
            >
              Acknowledge
            </Button>
          </Box>
        ))}
      {isTakeOverEnabled &&
        (isRebrand ? (
          <Box mt='4'>
            <DarkButton
              colorScheme={colorScheme}
              data-testid='incidentDrawer_feed_takeOverAndAcknowledge'
              isLoading={isLoadingCreateIncidentAction}
              onClick={onTakeOverOpen}
              size='lg'
              variant='primary'
            >
              Take Over & Acknowledge
            </DarkButton>
          </Box>
        ) : (
          <Box bg='#fff' mt='4' pos='relative'>
            <Button
              data-testid='incidentDrawer_feed_takeOverAndAcknowledge'
              isLoading={isLoadingCreateIncidentAction}
              loadingText='Taking over...'
              onClick={onTakeOverOpen}
            >
              Take Over & Acknowledge
            </Button>
          </Box>
        ))}
      {isCanReleaseEnabled &&
        (isRebrand ? (
          <Box mt='4'>
            <DarkButton
              colorScheme={colorScheme}
              data-testid='incidentDrawer_feed_release'
              isLoading={isLoadingCreateIncidentAction}
              onClick={onReleaseOpen}
              size='lg'
              variant='secondary'
            >
              Release
            </DarkButton>
          </Box>
        ) : (
          <Box bg='#fff' mt='4' pos='relative'>
            <Button
              data-testid='incidentDrawer_feed_release'
              leftIcon={<Icon as={Share} boxSize={6} strokeColor='#fff' />}
              loadingText='Releasing...'
              onClick={onReleaseOpen}
              variant='danger'
            >
              Release
            </Button>
          </Box>
        ))}
      {isReleaseOpen && isCanReleaseEnabled && (
        <ReleaseIncidentModal
          displayId={incident?.displayId}
          incidentName={incident?.name}
          isLoading={isLoadingCreateIncidentAction}
          isOpen={isReleaseOpen}
          onClose={onReleaseClose}
          onSubmit={onReleaseClick}
        />
      )}
      {isTakeOverOpen && isTakeOverEnabled && (
        <TakeOverIncidentModal
          currentOwner={currentOwner}
          displayId={incident?.displayId}
          incidentName={incident?.name}
          isLoading={isLoadingCreateIncidentAction}
          isOpen={isTakeOverOpen}
          nextAction={IncidentActionType.AcknowledgementByOperator}
          onClose={onTakeOverClose}
          onSubmit={onTakeOverClick}
        />
      )}
    </>
  )
}
